import { useState, useEffect, useMemo, useContext } from "react";
import dayjs from "dayjs";
import { Employee, ScheduleType, Day } from "../../../types/schedule";
import { LocationType } from "../../../components/schedule/modal/location-types";
import { CompanyContext } from "../../../contexts/company-context";
import { BranchOperation } from "../../../serviceoperation/BranchOperation";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";

export const useWeekView = () => {
  const [location, setLocation] = useState<LocationType[]>([]);
  const [currentWeek, setCurrentWeek] = useState(() =>
    dayjs().startOf("week").toDate()
  );
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleEmployees, setVisibleEmployees] = useState<Employee[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleType[]>([]);
  const [viewMode, setViewMode] = useState<"single" | "double">("single");
  const [loading, setLoading] = useState(false);

  const { selectedCompany } = useContext(CompanyContext);

  const branchOps = useMemo(() => new BranchOperation(), []);
  const scheduleOps = useMemo(() => new ScheduleOperation(), []);

  // API integration state
  const [useApiData, setUseApiData] = useState(false);

  // Convert ScheduleType data to Employee format for display
  const convertScheduleDataToEmployees = (
    scheduleData: ScheduleType[]
  ): Employee[] => {
    return scheduleData.map((schedule) => ({
      id: schedule.key.toString(),
      name: schedule.name,
      role: "Employee", // Default role since not provided in ScheduleType
      employeeID: schedule.key.toString(),
      firstName: schedule.initial,
      lastName: schedule.name.split(" ").slice(1).join(" ") || "",
      // Add default location/department for now
      locationId: "sf-downtown",
      departmentId: "engineering",
    }));
  };

  useMemo(() => {
    branchOps
      .getBranchAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        if (value.data) {
          setLocation(
            value.data.map((s) => ({
              key: s.branchID,
              name: s.name,
              color: s.color,
            }))
          );
        }
      });
  }, [selectedCompany]);

  // Filter employees based on search and selection
  useEffect(() => {
    const sourceEmployees = visibleEmployees;
    const filtered = sourceEmployees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        (selectedEmployees.length === 0 || selectedEmployees.includes(emp.id))
    );

    if (!useApiData) {
      setVisibleEmployees(filtered);
    }
  }, [searchQuery, selectedEmployees, useApiData]);

  const handleEmployeeChange = (ids: string[]) => {
    setSelectedEmployees(ids);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const navigateWeek = (direction: number) => {
    setCurrentWeek((current) => dayjs(current).add(direction, "week").toDate());
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(dayjs().startOf("week").toDate());
  };

  const cloneSchedule = (targetDate: Date) => {
    // Clone the entire schedule data to a new week
    const clonedData = scheduleData.map((schedule) => ({
      ...schedule,
      key: `${schedule.key}_cloned_${Date.now()}`,
      days: schedule.days.map((daySchedule) =>
        daySchedule.map((dayItem) => ({
          ...dayItem,
          key: `${dayItem.key}_cloned_${Date.now()}`,
        }))
      ),
    }));

    setScheduleData((prev) => [...prev, ...clonedData]);
  };

  const addEvent = (eventData: {
    employeeId: string;
    title: string;
    description: string;
    startTime: string;
    endTime: string;
    category: string;
    date: string;
  }) => {
    const targetDate = dayjs(eventData.date);
    const dayIndex = targetDate.day(); // 0 = Sunday, 1 = Monday, etc.

    // Convert 24-hour time to 12-hour format for display
    const startTime12 = dayjs(eventData.startTime, "HH:mm").format("h:mm A");
    const endTime12 = dayjs(eventData.endTime, "HH:mm").format("h:mm A");

    const newDayItem: Day = {
      key: `event_${Date.now()}`,
      time: `${startTime12} - ${endTime12}`,
      color: "#1890ff", // Default color
      secondcolor: getCategoryColor(eventData.category),
      tag: eventData.title,
      isPublished: true,
    };

    setScheduleData((prev) => {
      const updated = [...prev];
      const employeeIndex = updated.findIndex(
        (emp) => emp.key.toString() === eventData.employeeId
      );

      if (employeeIndex >= 0) {
        // Add to existing employee
        updated[employeeIndex].days[dayIndex].push(newDayItem);
      } else {
        // Create new employee entry
        const employee = visibleEmployees.find(
          (emp) => emp.id === eventData.employeeId
        );
        if (employee) {
          const newSchedule: ScheduleType = {
            key: employee.id,
            name: employee.name,
            initial: employee.firstName || employee.name[0],
            days: Array.from({ length: 7 }, (_, i) =>
              i === dayIndex ? [newDayItem] : []
            ),
          };
          updated.push(newSchedule);
        }
      }

      return updated;
    });
  };

  const getCategoryColor = (category: string): string => {
    switch (category.toLowerCase()) {
      case "scheduled":
        return "#52c41a"; // Green
      case "timeoffpending":
        return "#fa8c16"; // Orange
      case "timeoff":
        return "#ff4d4f"; // Red
      case "meeting":
        return "#1890ff"; // Blue
      default:
        return "#8c8c8c"; // Gray
    }
  };

  const handleViewModeChange = (mode: "single" | "double") => {
    setViewMode(mode);
  };

  // Main function to load API data - this accepts ScheduleType[] directly
  const loadApiScheduleData = (apiScheduleData: ScheduleType[]) => {
    setLoading(true);
    try {
      setScheduleData(apiScheduleData);

      // Convert to employee format for display
      const apiEmployees = convertScheduleDataToEmployees(apiScheduleData);

      // Update state
      setVisibleEmployees(apiEmployees);
      setUseApiData(true);
    } catch (error) {
      console.error("Error processing API data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to switch back to mock data
  /*const useMockData = () => {
    setUseApiData(false);
    setScheduleData(generateMockScheduleData());
    setVisibleEmployees(employees);
  };

  // Initialize with mock data
  useEffect(() => {
    if (!useApiData) {
      setScheduleData(generateMockScheduleData());
    }
  }, [useApiData]);*/

  // Function to get the current week in the format API
  const getCurrentWeekForApi = () => {
    return [dayjs(currentWeek).format("YYYY-MM-DD")];
  };

  return {
    currentWeek,
    selectedEmployees,
    scheduleData,
    visibleEmployees,
    viewMode,
    loading,
    useApiData,
    location,
    handleEmployeeChange,
    handleSearch,
    navigateWeek,
    goToCurrentWeek,
    cloneSchedule,
    addEvent,
    handleViewModeChange,
    loadApiScheduleData,
    //useMockData,
    setLoading,
    getCurrentWeekForApi,
  };
};
